import { Colors } from '@oy/uicore';
import {
  AttributedText,
  Button,
  Checkbox,
  FieldContainer,
  Message,
  Modal,
  RadioGroup,
  Text,
  TextField,
} from '@oy/uidesktop';
import { NumberFormatBase } from 'react-number-format';
import { WITHDRAWAL_TYPE_OPTIONS } from 'src/constants';
import { useTranslate } from 'src/hooks';
import { currencyInputFormat } from 'src/utilities/currency';
import { formatCurrency } from 'src/utilities/helpers';
import { ErrorBoundary } from '../ErrorBoundary';
import styles from './WithdrawalModal.module.css';
import { ConfirmWithdrawalModal } from './components';
import { DetailItem } from './components/DetailItem/DetailItem';
import { InstantWithdrawalError } from './components/InstantWithdrawalError/InstantWithdrawalError';
import { useWithdrawalModalImpl } from './useWithdrawalModalImpl';

export const WithdrawalModal = () => {
  const { getTranslation } = useTranslate();

  const {
    state,
    getWithdrawalType,
    gotoAutoWithdrawal,
    handleChangeAmount,
    handleChangeIsManual,
    handleSubmitWithdrawal,
    handleWithdrawAllChange,
    toggleModal,
    toggleShowConfirmWithdrawal,
  } = useWithdrawalModalImpl();

  const {
    adminFee,
    amount,
    receiverInfo,
    disableSubmit,
    errorMessage,
    formattedTime,
    isExceedInstantWithdrawalLimit,
    isInstantWithdrawalDisable,
    isNotInService,
    isUnderMaintenance,
    isWithdrawAll,
    isOpeningLessThanClosing,
    maxWithdrawalAmount,
    showAutoWithdrawalButton,
    showConfirmWithdrawal,
    visible,
  } = state;

  const renderWithdrawalContent = (e: {
    value: string;
    descriptionKey: string;
  }) => {
    if (e.value === 'manual') {
      return (
        <>
          <AttributedText
            value={getTranslation(e.descriptionKey)}
            textStyle={AttributedText.Style.BodyMdRegular}
          />
          {isUnderMaintenance && (
            <Message type={Message.Type.Warning} showIcon>
              <AttributedText
                value={getTranslation('manualWithdrawal_underMaintenance', {
                  bank: receiverInfo.bank.label,
                })}
                textStyle={AttributedText.Style.BodyMdRegular}
              />
            </Message>
          )}
        </>
      );
    }

    if (e.value === 'instant') {
      return (
        <InstantWithdrawalError
          isNotInService={isNotInService}
          isOpeningLessThanClosing={isOpeningLessThanClosing}
          formattedTime={formattedTime}
          isExceedLimit={isExceedInstantWithdrawalLimit}
        />
      );
    }

    if (!isInstantWithdrawalDisable) {
      return (
        <AttributedText
          value={getTranslation(e.descriptionKey)}
          textStyle={AttributedText.Style.BodyMdRegular}
        />
      );
    }

    return undefined;
  };

  return (
    <Modal
      show={visible}
      className={styles.container}
      closeOnBackdropClick
      onClose={toggleModal}
    >
      <Modal.Header showCloseButton>
        {getTranslation('common_txProduct_withdrawal')}
      </Modal.Header>

      <Modal.Body className={styles.body}>
        <ErrorBoundary domain="dashboard" componentName="WithdrawalModalForm">
          <div className={styles.body}>
            <Text textStyle={Text.Style.BodySmSemibold} color={Colors.BLACK40}>
              {getTranslation(
                'b2xAskRefundModal_amountWithdrawn',
              ).toUpperCase()}
            </Text>
            <Text textStyle={Text.Style.BodyXlSemibold}>
              {formatCurrency(maxWithdrawalAmount)}
            </Text>
          </div>
          <div className={styles.form}>
            <NumberFormatBase
              customInput={TextField}
              isHorizontal
              label={getTranslation('withdraw_amountWithdrawn')}
              format={currencyInputFormat}
              name="amount"
              placeholder="Rp 0"
              value={amount}
              onChange={handleChangeAmount}
              errorMessage={errorMessage}
            />
            <FieldContainer label=" " isHorizontal>
              <Checkbox
                id="All Funds"
                title={getTranslation('withdraw_withdrawAll')}
                onChange={handleWithdrawAllChange}
                checked={isWithdrawAll}
                name="withdrawAll"
                type="box"
              />
            </FieldContainer>
            <RadioGroup
              options={WITHDRAWAL_TYPE_OPTIONS.map(e => ({
                value: e.value,
                label: getTranslation(e.labelKey),
                isDisabled:
                  (e.value === 'instant' && isInstantWithdrawalDisable) ||
                  (e.value === 'manual' && isUnderMaintenance),
                additionalInput: renderWithdrawalContent(e),
                id: e.id,
              }))}
              label={getTranslation('common_withdrawalType')}
              isHorizontal
              name="isManual"
              value={getWithdrawalType()}
              onChange={handleChangeIsManual}
            />
          </div>
          <div className={styles.detailContainer}>
            <Text textStyle={Text.Style.BodySmSemibold} color={Colors.BLACK40}>
              {getTranslation('withdrawal_withdrawalDetail').toUpperCase()}
            </Text>
            <DetailItem
              item={getTranslation('withdraw_amountWithdrawn')}
              amount={amount}
            />
            <DetailItem
              item={getTranslation('common_adminFee')}
              amount={adminFee}
            />
            <hr className={styles.hr} />
            <DetailItem
              item={getTranslation('withdraw_totalAmountWithdrawn')}
              amount={amount + adminFee}
              bold
            />
          </div>

          <ConfirmWithdrawalModal
            onSubmit={handleSubmitWithdrawal}
            show={showConfirmWithdrawal}
            onClose={toggleShowConfirmWithdrawal}
            receiverInfo={receiverInfo}
          />
        </ErrorBoundary>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.buttonContainer}>
          <Button
            disabled={amount === 0 || disableSubmit}
            name="Withdraw"
            id="btnWithdraw"
            onClick={toggleShowConfirmWithdrawal}
          >
            {getTranslation('common_withdraw')}
          </Button>
          {showAutoWithdrawalButton && (
            <Button buttonType={Button.Type.Text} onClick={gotoAutoWithdrawal}>
              {getTranslation('b2xAskRefundModal_setWithdrawScheduler')}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};
